// src/contexts/ClientContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const ClientContext = createContext();

export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};

export const ClientProvider = ({ children }) => {
  const [clienteAtual, setClienteAtual] = useState({
    codCliente: null,
    nomeCliente: null,
    dadosCompletos: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Carregar cliente do localStorage ao montar
  useEffect(() => {
    const clienteSalvo = localStorage.getItem('clientSession');
    if (clienteSalvo) {
      try {
        const cliente = JSON.parse(clienteSalvo);
        setClienteAtual(cliente);
        console.log('Cliente carregado da sessão:', cliente.codCliente);
      } catch (err) {
        console.error('Erro ao carregar cliente da sessão:', err);
        localStorage.removeItem('clientSession');
      }
    }
  }, []);

  // Definir novo cliente
  const setCliente = useCallback(async (codigo) => {
    // Validar formato (5 dígitos)
    if (!/^\d{5}$/.test(codigo)) {
      setError('Código do cliente deve ter 5 dígitos numéricos');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('Buscando dados do cliente:', codigo);
      const response = await axios.get(`/api/clients/${codigo}`);

      if (response.data.success && response.data.data) {
        const dadosCliente = response.data.data;
        
        // Extrair nome do cliente (priorizar campo 'nome' ou 'name')
        const nome = dadosCliente.nome || dadosCliente.name || dadosCliente.Nome || 'Cliente';
        
        const novoCliente = {
          codCliente: codigo,
          nomeCliente: nome,
          dadosCompletos: dadosCliente
        };

        setClienteAtual(novoCliente);
        localStorage.setItem('clientSession', JSON.stringify(novoCliente));
        
        console.log('✅ Cliente definido:', codigo, '-', nome);
        return true;
      } else {
        setError('Cliente não encontrado');
        return false;
      }
    } catch (err) {
      console.error('Erro ao buscar cliente:', err);
      const mensagem = err.response?.data?.message || 'Erro ao buscar dados do cliente';
      setError(mensagem);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Limpar cliente da sessão
  const clearCliente = useCallback(() => {
    setClienteAtual({
      codCliente: null,
      nomeCliente: null,
      dadosCompletos: null
    });
    localStorage.removeItem('clientSession');
    setError(null);
    console.log('Cliente removido da sessão');
  }, []);

  // Atualizar dados do cliente atual
  const refreshCliente = useCallback(async () => {
    if (clienteAtual.codCliente) {
      return await setCliente(clienteAtual.codCliente);
    }
    return false;
  }, [clienteAtual.codCliente, setCliente]);

  const value = {
    clienteAtual,
    loading,
    error,
    setCliente,
    clearCliente,
    refreshCliente,
    hasCliente: !!clienteAtual.codCliente
  };

  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
};